import { Segment } from '~/Helpers/Segment.helper';
import { SegmentName } from '~/Constants/Segment.constants';
import { ILocation } from '~/Models/DTO/Location.models';
import { useLocations } from '~/store/provider';
import {
  useCreatedPartnerPortalChecklist,
  useNavigationContext,
  useNavigation,
  useHasAccess,
  AccessList,
} from '~/Hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentTour, setProductTour } from '~/store/app/productTours';
import { bindActionCreators } from 'redux';
import { Tour } from '~/Models/interfaces/Tour.enum';
import { Route } from '~/Constants/Routes.constants';
import { ExtendedLocation } from '~/Screens/Settings/ProviderLocations/LocationEdit/ExtendedLocation.model';
import { UpdateLocation } from './LocationEditTypes';
import { assignLocationsToMerchant } from '~/requests/providerService/useRequestMerchants';
import { useUpdateDefaultCancellationWindow } from '~/requests/eventService/defaultCancellationWindow/useDefaultCancellationWindow';
import { useUpdateFirstFreeCheckin } from '~/requests/providerService/useRequestFirstFreeCheckin/useRequestFirstFreeCheckin';

const updatedLocationFormatter = (currentLocation: ExtendedLocation | ILocation) => {
  const openingHours = currentLocation?.openingHours;
  const description = currentLocation?.openingHours?.description;

  return {
    ...currentLocation,
    openingHours: {
      weekdays: description ? {} : openingHours?.weekdays,
      description,
    },
  };
};

export const useUpdateLocation = () => {
  const dispatch = useDispatch();
  const { updateLocation: updateLocationAction, updateLocationError } = useLocations();
  const { hasAccess } = useHasAccess();
  const updateDefaultCancellationWindow = useUpdateDefaultCancellationWindow();
  const { mutate: updateFirstFreeCheckin } = useUpdateFirstFreeCheckin();

  const {
    createdPartnerChecklist,
    updateCreatedPartnerChecklist,
  } = useCreatedPartnerPortalChecklist();
  const currentTour = useSelector(selectCurrentTour);
  const setTour = bindActionCreators(setProductTour, dispatch);
  const { blockNavigation } = useNavigationContext();
  const { dangerouslyPushBack } = useNavigation();

  const updateLocation = async ({
    setIsSubmitDisabled,
    currentLocation,
    setIsLoading,
  }: UpdateLocation) => {
    setIsSubmitDisabled(false);
    const updatedLocation = updatedLocationFormatter(currentLocation);
    let hasError = false;
    const setRawData = (response: any) => {
      if (response.status >= 400) {
        hasError = true;
      }
    };

    try {
      Segment.track(SegmentName.EDIT_LOCATION);
      if (updatedLocation?.merchant && !updatedLocation?.venueAddressId) {
        updateLocationError(new Error('Venue address id is missing or zero'));
        setIsLoading(false);
        hasError = true;
      } else {
        await updateLocationAction(updatedLocation as ILocation, setRawData);

        if (hasAccess(AccessList.DEFAULT_CANCELATION_WINDOW)) {
          await updateDefaultCancellationWindow({
            locationId: currentLocation.uuid,
            defaultCancellationWindow: currentLocation.defaultCancellationWindow || 0,
          });
        }

        await updateFirstFreeCheckin({
          locationId: currentLocation.uuid,
          isFirstCheckInFree: currentLocation.firstFreeCheckin || false,
        });

        if (updatedLocation?.merchant) {
          const venueAddressId = updatedLocation?.venueAddressId;
          const locationUuid = updatedLocation?.uuid;
          await assignLocationsToMerchant({
            uuid: updatedLocation?.merchant || '',
            location: venueAddressId || 0,
            locationuuid: locationUuid || '',
          });
        }
      }
    } catch (error) {
      setIsLoading(false);
      hasError = true;
      console.error('failed to save the location', error);
    }
    if (!createdPartnerChecklist?.studioDataEdited) {
      updateCreatedPartnerChecklist({ studioDataEdited: new Date().toISOString() });
      Segment.track(SegmentName.CREATED_PARTNER_PORTAL_CHECKLIST_STUDIO_UPDATED);
    }
    if (currentTour === Tour.STUDIO_CREATION_PENDING) {
      setTour(Tour.STUDIO_CREATION_DONE);
    }

    if (!hasError) {
      blockNavigation(false);
      dangerouslyPushBack(Route.PROVIDER_LOCATIONS);
    }
  };

  return { updateLocation };
};
